import { linkOptions, useNavigate, useSearch } from '@tanstack/react-router';

import { CustomerGroupMessage } from '../../../../types/CustomerGroupMessage';
import { CenteredPagination } from '../../../cdl/Pagination/CenteredPagination';
import { TableBuilder } from '../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../cdl/TableBuilder/TableBuilderColumn';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { formatDateTime } from '../../../common/helpers/formatDateTime.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { IconCheck } from '../../../common/icons/cdl/Check';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { NoSearchResults } from '../../../common/Search/NoSearchResults/NoSearchResults';
import { useCustomerGroupsPagination } from '../hooks/useCustomerGroupsPagination';

export const CustomerGroupsTable = () => {
    const search = useSearch({ from: '/_app/customer-groups/' });
    const navigate = useNavigate({ from: '/customer-groups' });
    const customerGroupsQuery = useCustomerGroupsPagination(search.searchQuery, search.page);

    if (customerGroupsQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (customerGroupsQuery.isError) {
        return <AdvancedError />;
    }

    if (customerGroupsQuery.data.content.length === 0) {
        return <NoSearchResults variant="page" />;
    }

    return (
        <div>
            <TableBuilder
                data={customerGroupsQuery.data.content}
                rowLink={(row: CustomerGroupMessage) =>
                    linkOptions({ to: '/customer-groups/$groupId', params: { groupId: row.id } })
                }
                grow={false}
            >
                <TableBuilderColumn<CustomerGroupMessage> header={translate('customergroup.name')} width="50%" truncate>
                    {(row) => row.name}
                </TableBuilderColumn>
                <TableBuilderColumn<CustomerGroupMessage> header={translate('customer.datecreated')}>
                    {(row) => formatDateTime({ date: row.dateCreated })}
                </TableBuilderColumn>
                <TableBuilderColumn<CustomerGroupMessage> header={translate('customergroup.testcompany')}>
                    {(row) => (row.testCompany ? <IconCheck width={16} height={16} /> : null)}
                </TableBuilderColumn>
            </TableBuilder>

            <CenteredPagination
                currentPage={search.page}
                pageTotal={customerGroupsQuery.pageTotal}
                onPageChange={(page) => {
                    navigate({
                        search: (previousSearch) => ({ ...previousSearch, page }),
                    });
                }}
            />
        </div>
    );
};
